import { Component, h, tag } from "omi";
import { Router } from "@vaadin/router";

import {
  isPortfolioItemFile,
  isPortfolioItemLink,
  isPortfolioItemProject,
  PortfolioDocument,
} from "@/pages/portfolio/store/models";
import { NWButton } from "@/components/atoms/nw-button";
import { DropdownItem, NWDropdown } from "@/components/atoms/nw-dropdown";
import { NWIcon } from "@/components/atoms/nw-icon";
import { openModal } from "@/components/modals/events";
import { PortfolioStore } from "@/pages/portfolio/store";
import { tailwind } from "@/tailwind";

type Props = { name: string; data: PortfolioDocument };

@tag("portfolio-grid-tile")
export class GridTile extends Component<Props> {
  static css = [tailwind];

  private dropdownItems: DropdownItem[] = [
    { type: "button", callback: () => this.openEditModal(), text: "Edit project", icon: "edit" },
    { type: "button", callback: () => this.openDeleteModal(), text: "Delete project", icon: "trash" },
  ];

  private openDeleteModal = () => {
    if (!PortfolioStore.isEditable()) return;

    openModal("portfolioDelete", this.props.data);
  };

  private openEditModal = () => {
    if (!PortfolioStore.isEditable()) return;

    openModal("portfolioEdit", { type: "edit", document: this.props.data });
  };

  private formatUrl(url: string): string {
    let formattedUrl = url.trim();

    if (!formattedUrl.startsWith("http://") && !formattedUrl.startsWith("https://")) {
      formattedUrl = `https://${formattedUrl}`;
    }

    return formattedUrl;
  }

  private handleDocumentClick = (e: MouseEvent) => {
    if (isPortfolioItemFile(this.props.data) || isPortfolioItemProject(this.props.data)) {
      e.preventDefault();

      if (PortfolioStore.isOwner()) {
        Router.go(`portfolio/documents/${this.props.data.id}`);
      } else {
        const viewing = PortfolioStore.viewing.value;
        if (!viewing) return;
        Router.go(`${viewing}/portfolio/documents/${this.props.data.id}`);
      }
    }
  };

  render({ name, data }: Props) {
    const Tag = { name: isPortfolioItemLink(data) ? "a" : "button" };
    const href = isPortfolioItemLink(data) && data.url?.url ? this.formatUrl(data.url.url) : undefined;

    return (
      <Tag.name
        className="group w-full aspect-square bg-leather rounded-[40px] border border-gray-200
          flex flex-col justify-between items-center py-8 px-6 space-y-4
          hover:scale-[1.02] hover:shadow-lg
          transition-all duration-300 ease-out animate-fadeIn"
        href={href}
        onClick={this.handleDocumentClick}
        target={isPortfolioItemLink(data) ? "_blank" : undefined}
      >
        <div className="flex items-center justify-center relative w-full h-10">
          <p className="text-brand-50 font-semibold">PROJECT</p>

          {PortfolioStore.isEditable() && (
            <div className="absolute -right-2.5 top-0 opacity-100 group-hover:opacity-100 transition-opacity duration-300">
              <NWDropdown
                items={this.dropdownItems}
                withBorder={false}
                customTrigger={true}
                withAnimations
                width="fit"
                isolateMouseEvents
                horizontalOrientation="right"
              >
                <NWButton size="icon" type="icon" theme="dark">
                  <NWIcon name="dots-vertical" width={24} height={24} />
                </NWButton>
              </NWDropdown>
            </div>
          )}
        </div>

        <div className="self-stretch text-center text-white text-2xl font-semibold">{name}</div>

        <div
          className="p-2.5 rounded-full border-2 border-white justify-center items-center inline-flex
          group-hover:bg-white transition-colors duration-300"
        >
          <NWIcon
            name="arrow-right"
            className="text-brand-50 group-hover:text-primary transition-colors duration-300"
            width={28}
            height={28}
          />
        </div>
      </Tag.name>
    );
  }
}
